import { mockDelay, mockHttpError } from './withMockFallback';

/**
 * Mock заявок на изменение (C3 Change Requests). DTO повторяет production:
 * идентичность — `request_id`, тип — `request_type`, идентификатор зоны лежит
 * верхним полем `zone_id`, остальные данные — в `payload`.
 *
 * Заявка применяется ровно один раз: повторный approve/reject уже решённой
 * заявки даёт 409, как и на backend. Автоматически повторять применение UI
 * не имеет права.
 */

const store = new Map();
let idCounter = 0;

export const createChangeRequestMock = (payload = {}) => {
  idCounter += 1;
  const id = `cr-${idCounter}`;

  const request = {
    request_id: id,
    status: 'pending',
    camera_id: payload.camera_id,
    camera_name: payload.camera_name || '',
    request_type: payload.request_type,
    zone_id: payload.zone_id ?? null,
    payload: payload.payload ?? {},
    requested_by_user_id: 'mock-user',
    impact: { features_to_enable: [], features_to_disable: [], affected_rules: [] },
    created_at: new Date().toISOString(),
  };

  store.set(id, request);
  return mockDelay(request);
};

export const listAdminChangeRequestsMock = (params = {}) => {
  const all = [...store.values()].filter((request) => {
    if (params.status && request.status !== params.status) return false;
    if (params.camera_id && Number(request.camera_id) !== Number(params.camera_id)) return false;
    return true;
  });

  const limit = params.limit ?? 50;
  const offset = params.offset ?? 0;

  return mockDelay({
    requests: all.slice(offset, offset + limit),
    total: all.length,
    limit,
    offset,
  });
};

export const getAdminChangeRequestMock = (id) => {
  const request = store.get(id);
  if (!request) return Promise.reject(mockHttpError(404, { detail: 'change_request_not_found' }));
  return mockDelay(request);
};

const requirePending = (id) => {
  const request = store.get(id);
  if (!request) return { error: mockHttpError(404, { detail: 'change_request_not_found' }) };

  // Заявку, которую уже разобрали, применить повторно нельзя.
  if (request.status !== 'pending') {
    return { error: mockHttpError(409, { detail: 'change_request_not_pending', status: request.status }) };
  }
  return { request };
};

export const approveChangeRequestMock = (id, payload = {}) => {
  const { request, error } = requirePending(id);
  if (error) return Promise.reject(error);

  request.status = 'approved';
  request.review_note = payload.note ?? null;
  return mockDelay({ ...request });
};

export const rejectChangeRequestMock = (id, payload = {}) => {
  const { request, error } = requirePending(id);
  if (error) return Promise.reject(error);

  request.status = 'rejected';
  request.review_note = payload.note ?? payload.reason ?? null;
  return mockDelay({ ...request });
};

/** ТЗ 27: summary считает pending прямо по store — та же authoritative правда, что и список. */
export const getChangeRequestSummaryMock = () => mockDelay({
  pending_count: [...store.values()].filter((request) => request.status === 'pending').length,
});

/** ТЗ 30: «мои заявки» в mock — тот же store, без фильтра по автору (mock один пользователь). */
export const listMyChangeRequestsMock = () => mockDelay({
  requests: [...store.values()],
});

/** Только для тестов. */
export const resetChangeRequestsMock = () => {
  store.clear();
  idCounter = 0;
};
